// Session cache for Databricks data
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const CACHE_KEYS = {
    CATALOGS: 'databricks_catalogs',
    SCHEMAS: 'databricks_schemas',
    TABLES: 'databricks_tables'
} as const;

// Helper functions to generate granular cache keys
export const getCacheKey = {
    catalogs: () => CACHE_KEYS.CATALOGS,
    schemas: (catalog: string) => `${CACHE_KEYS.SCHEMAS}_${catalog}`,
    tables: (catalog: string, schema: string) => `${CACHE_KEYS.TABLES}_${catalog}_${schema}`
};

interface CacheItem<T> {
    data: T;
    timestamp: number;
    key?: string; // For schemas/tables that depend on parent selection
}

export const getCachedData = <T>(cacheKey: string, dependencyKey?: string): T | null => {
    try {
        const cached = sessionStorage.getItem(cacheKey);
        if (!cached) return null;

        const item: CacheItem<T> = JSON.parse(cached);
        const isExpired = Date.now() - item.timestamp > CACHE_DURATION;
        const keyMismatch = dependencyKey && item.key !== dependencyKey;

        if (isExpired || keyMismatch) {
            sessionStorage.removeItem(cacheKey);
            return null;
        }

        return item.data;
    } catch {
        return null;
    }
};

export const setCachedData = <T>(cacheKey: string, data: T, dependencyKey?: string): void => {
    try {
        const item: CacheItem<T> = {
            data,
            timestamp: Date.now(),
            key: dependencyKey
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(item));
    } catch {
        // Ignore cache errors
    }
};

export const clearDatabricksCache = (): void => {
    try {
        // Clear all Databricks cache entries by pattern matching
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
            if (key.startsWith('databricks_')) {
                sessionStorage.removeItem(key);
            }
        });
    } catch {
        // Ignore cache errors
    }
};

export const clearSpecificCache = (type: 'catalogs' | 'schemas' | 'tables', catalog?: string, schema?: string): void => {
    try {
        switch (type) {
            case 'catalogs':
                sessionStorage.removeItem(getCacheKey.catalogs());
                break;
            case 'schemas':
                if (catalog) {
                    sessionStorage.removeItem(getCacheKey.schemas(catalog));
                }
                break;
            case 'tables':
                if (catalog && schema) {
                    sessionStorage.removeItem(getCacheKey.tables(catalog, schema));
                }
                break;
        }
    } catch {
        // Ignore cache errors
    }
};